import { BusinessOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { Business } from '../ClientProfile';

import { RootState } from 'redux/store';

const BusinessOwnership = ({
    businessData,
    mobile,
}: {
    businessData: null | Business[];
    mobile: boolean;
}) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    function formatCurrency(amount: number | null) {
        if (amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        } else {
            return '';
        }
    }

    function formatDate(date: Date | null) {
        if (date) {
            const stringDate = date.toString();
            const [year, month, day] = stringDate.split('-');
            const months =
                language === 'EN'
                    ? [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                      ]
                    : [
                          'Janvier',
                          'Février',
                          'Mars',
                          'Avril',
                          'Mai',
                          'Juin',
                          'Juillet',
                          'Août',
                          'Septembre',
                          'Octobre',
                          'Novembre',
                          'Décembre',
                      ];

            const monthName = months[parseInt(month, 10) - 1];
            const formattedDate = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    return (
        <>
            {businessData && businessData.length > 0 ? (
                <>
                    {businessData.map((business: Business, index: number) => {
                        // Same as AssessmentV2/BusinessOwnership — region/city for destination countries only.
                        const showRegionAndCity =
                            business.country === 'Australia' ||
                            business.country === 'Canada' ||
                            business.country === 'United Kingdom';

                        return (
                            <div key={index}>
                                {businessData.length > 1 && (
                                    <S.Title>{`${t('business', { lng: language })} ${
                                        index + 1
                                    }`}</S.Title>
                                )}

                                <S.InfoItem>
                                    <Tooltip title={t('current', { lng: language })}>
                                        <S.Label>{t('current', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            business.current
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })
                                        }
                                    >
                                        <S.Info mobile={mobile}>
                                            {business.current
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('startDate', { lng: language })}>
                                        <S.Label>{t('startDate', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatDate(business.start)}>
                                        <S.Info mobile={mobile}>
                                            {formatDate(business.start)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>

                                <S.InfoItem>
                                    <Tooltip title={t('endDate', { lng: language })}>
                                        <S.Label>{t('endDate', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatDate(business.end)}>
                                        <S.Info mobile={mobile}>{formatDate(business.end)}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('country', { lng: language })}>
                                        <S.Label>{t('country', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={business.country}>
                                        <S.Info mobile={mobile}>{business.country}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                {showRegionAndCity && (
                                    <>
                                        <S.InfoItem>
                                            <Tooltip title={t('region', { lng: language })}>
                                                <S.Label>{t('region', { lng: language })}</S.Label>
                                            </Tooltip>
                                            <Tooltip title={business.region}>
                                                <S.Info mobile={mobile}>{business.region}</S.Info>
                                            </Tooltip>
                                        </S.InfoItem>
                                        <S.InfoItem>
                                            <Tooltip title={t('city', { lng: language })}>
                                                <S.Label>{t('city', { lng: language })}</S.Label>
                                            </Tooltip>
                                            <Tooltip title={business.city}>
                                                <S.Info mobile={mobile}>{business.city}</S.Info>
                                            </Tooltip>
                                        </S.InfoItem>
                                    </>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('businessType', { lng: language })}>
                                        <S.Label mobile={mobile}>
                                            {t('businessType', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={business.type}>
                                        <S.Info mobile={mobile}>{business.type}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('shareOfTheCompany', { lng: language })}>
                                        <S.Label>
                                            {mobile
                                                ? t('share', { lng: language })
                                                : t('shareOfTheCompany', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={`${business.percent_ownership}%`}>
                                        <S.Info
                                            mobile={mobile}
                                        >{`${business.percent_ownership}%`}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>

                                <S.InfoItem>
                                    <Tooltip title={t('numberOfEmployees', { lng: language })}>
                                        <S.Label>
                                            {mobile
                                                ? t('employees', { lng: language })
                                                : t('numberOfEmployees', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={business.number_employees}>
                                        <S.Info mobile={mobile}>{business.number_employees}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('annualSales(USD)', { lng: language })}>
                                        <S.Label mobile={mobile}>
                                            {t('annualSales(USD)', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatCurrency(business.annual_sales)}>
                                        <S.Info mobile={mobile}>
                                            {formatCurrency(business.annual_sales)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('netProfit(USD)', { lng: language })}>
                                        <S.Label mobile={mobile}>
                                            {t('netProfit(USD)', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatCurrency(business.net_profit)}>
                                        <S.Info mobile={mobile}>
                                            {formatCurrency(business.net_profit)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('netCompanyValue(USD)', { lng: language })}>
                                        <S.Label mobile={mobile}>
                                            {t('netCompanyValue(USD)', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatCurrency(business.net_company_value)}>
                                        <S.Info mobile={mobile}>
                                            {formatCurrency(business.net_company_value)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <ProfileFields
                                    record={business}
                                    keys={['currency']}
                                    mobile={mobile}
                                />
                            </div>
                        );
                    })}
                </>
            ) : (
                <Result
                    icon={<BusinessOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No business ownership details have been added yet."
                />
            )}
        </>
    );
};

export default BusinessOwnership;
